import { useEffect, useMemo, useState } from "react";
import {
    Cpu, Network, Activity, ShieldCheck, CheckCircle2, Zap,
    Database, Layers, Globe2, ArrowUpRight, Server,
    HardDrive, Sparkles, Workflow, Repeat, ServerCog, GitBranch,
} from "lucide-react";

/**
 * DeCloudX · Protocol Console
 * ---------------------------------------------------------------
 * One premium product mockup with tabs across the top.
 * Reflects the FULL protocol — the chain is one tab inside it.
 *
 * Tabs (order matters):
 *   1) Orchestration  (DEFAULT — hybrid 70/30 routing)
 *   2) Compute        (containerized workloads · DePIN nodes)
 *   3) Storage        (encrypted shards · erasure-coded)
 *   4) AI             (GPU inference · settled in $DCX)
 *   5) Layer-1 Settlement (EVM chain · finality · gas)
 *   6) DNS            (DCX Name Service · on-chain routing)
 */

// --- Tab definitions --------------------------------------------
const TABS = [
    { id: "orchestration", label: "Orchestration",       icon: Workflow, accent: "#8B5CF6" },
    { id: "compute",       label: "Compute",             icon: Server,   accent: "#06B6D4" },
    { id: "storage",       label: "Storage",             icon: HardDrive, accent: "#0EA5E9" },
    { id: "ai",            label: "AI",                  icon: Cpu,      accent: "#2563EB" },
    { id: "layer1",        label: "Layer-1 Settlement",  icon: Layers,   accent: "#7C3AED" },
    { id: "dns",           label: "DNS",                 icon: Globe2,   accent: "#0891B2" },
];

// --- Small primitives -------------------------------------------
function LiveDot({ color = "var(--brand-cyan)" }) {
    return (
        <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: color }} />
            <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: color }} />
        </span>
    );
}

function StatusPill({ label = "LIVE", color = "var(--brand-cyan)" }) {
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full font-mono text-[9px] uppercase tracking-[0.18em] border"
            style={{ borderColor: "var(--line-strong)", color: "var(--ink-2)", background: "var(--card-muted)" }}
        >
            <LiveDot color={color} />
            {label}
        </span>
    );
}

function KV({ k, v, accent, mono = true }) {
    return (
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-line-faint last:border-0">
            <span className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-faint-ink">{k}</span>
            <span
                className={`text-[12.5px] font-semibold ${mono ? "font-mono" : ""}`}
                style={{ color: accent || "var(--ink)" }}
            >
                {v}
            </span>
        </div>
    );
}

function NodeTile({ icon: Icon, label, value, color }) {
    return (
        <div className="rounded-lg border border-line bg-card-muted px-3 py-2.5" data-testid={`console-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                    {Icon && (
                        <span
                            className="w-4 h-4 rounded-md flex items-center justify-center"
                            style={{ background: `${color}14`, color, border: `1px solid ${color}26` }}
                        >
                            <Icon size={10} strokeWidth={1.8} />
                        </span>
                    )}
                    <span className="font-mono text-[9px] uppercase tracking-[0.16em] text-faint-ink">{label}</span>
                </div>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
            </div>
            <div className="font-heading font-bold text-[14px] text-ink leading-none tracking-tight tabular-nums">{value}</div>
        </div>
    );
}

function ActivityRow({ icon: Icon, color, title, meta, status, statusColor }) {
    return (
        <div className="flex items-center gap-3 px-5 py-2.5 border-b border-line-faint last:border-0">
            <div
                className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                style={{ background: `${color}12`, color, border: `1px solid ${color}26` }}
            >
                <Icon size={11} strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[12px] font-medium text-ink truncate">{title}</div>
                <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-faint-ink mt-0.5">{meta}</div>
            </div>
            <span
                className="font-mono text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-md border whitespace-nowrap"
                style={{ borderColor: `${statusColor}30`, color: statusColor, background: `${statusColor}0d` }}
            >
                {status}
            </span>
        </div>
    );
}

// ====== PANEL: ORCHESTRATION (default) ==========================
function PanelOrchestration() {
    return (
        <div data-testid="console-panel-orchestration">
            {/* Hybrid 70/30 visual bar */}
            <div className="px-5 pt-4 pb-3 border-b border-line-faint">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-faint-ink mb-1">
                            Hybrid Workload Routing
                        </div>
                        <div className="font-heading font-bold text-[13px] text-ink leading-tight tracking-tight">
                            70% Public DePIN · 30% Private Fallback
                        </div>
                    </div>
                    <span
                        className="shrink-0 font-mono text-[9px] uppercase tracking-[0.16em] px-2 py-0.5 rounded-md border whitespace-nowrap"
                        style={{ borderColor: "#05966930", color: "#059669", background: "#0596690d" }}
                    >
                        Healthy
                    </span>
                </div>

                {/* Stacked horizontal bar — 70 / 30 */}
                <div className="relative h-2 w-full rounded-full overflow-hidden border border-line bg-card">
                    <div
                        className="absolute inset-y-0 left-0 rounded-l-full"
                        style={{
                            width: "70%",
                            background: "linear-gradient(90deg, #8B5CF6 0%, #06B6D4 100%)",
                        }}
                    />
                    <div
                        className="absolute inset-y-0 right-0 rounded-r-full"
                        style={{
                            width: "30%",
                            background: "repeating-linear-gradient(45deg, rgba(124,58,237,0.55) 0 6px, rgba(124,58,237,0.25) 6px 12px)",
                        }}
                    />
                </div>
            </div>

            {/* Key/values — only 4 essential rows */}
            <KV k="Autoscaling"  v="Enabled"  accent="var(--success)" />
            <KV k="Failover"     v="Active"   accent="var(--success)" />
            <KV k="Node Health"  v="99.97%"   accent="var(--success)" />
            <KV k="Recovery"     v="< 10s" />

            {/* Sub-cards: protocol health at a glance */}
            <div className="grid grid-cols-2 gap-2 p-3 border-t border-line bg-card-muted/30">
                <NodeTile icon={Server}     label="Compute"        value="8,420 active"   color="#06B6D4" />
                <NodeTile icon={HardDrive}  label="Storage"        value="RS 16/10"        color="#0EA5E9" />
                <NodeTile icon={Sparkles}   label="AI Jobs"        value="Settled · $DCX"  color="#8B5CF6" />
                <NodeTile icon={Layers}     label="Layer-1"        value="1.8s finality"   color="#7C3AED" />
            </div>
        </div>
    );
}

// ====== PANEL: COMPUTE ==========================================
function PanelCompute() {
    return (
        <div data-testid="console-panel-compute">
            <div className="grid grid-cols-2 gap-2 p-3 pb-2">
                <NodeTile icon={Server}      label="Compute Nodes"   value="8,420" color="#06B6D4" />
                <NodeTile icon={Cpu}         label="GPU Nodes"       value="412"   color="#8B5CF6" />
                <NodeTile icon={HardDrive}   label="Storage Nodes"   value="3,108" color="#2563EB" />
                <NodeTile icon={ShieldCheck} label="Validator Nodes" value="64"    color="#172554" />
            </div>

            <div className="grid grid-cols-3 border-t border-line bg-card-muted">
                <div className="px-3 py-2.5 border-r border-line">
                    <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-faint-ink">Regions</div>
                    <div className="font-mono text-[12.5px] font-semibold text-ink mt-0.5 tabular-nums">62</div>
                </div>
                <div className="px-3 py-2.5 border-r border-line">
                    <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-faint-ink">Uptime</div>
                    <div className="font-mono text-[12.5px] font-semibold mt-0.5 tabular-nums" style={{ color: "var(--success)" }}>99.97%</div>
                </div>
                <div className="px-3 py-2.5">
                    <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-faint-ink">Containers</div>
                    <div className="font-mono text-[12.5px] font-semibold text-ink mt-0.5 tabular-nums">24,180</div>
                </div>
            </div>

            <KV k="Runtime"     v="containerd · OCI" />
            <KV k="Routing"     v="Public DePIN → Private Fallback" accent="var(--brand-violet)" />
            <KV k="Hybrid Split" v="70 / 30" accent="var(--brand-cyan)" />
        </div>
    );
}

// ====== PANEL: STORAGE ==========================================
function PanelStorage() {
    return (
        <div data-testid="console-panel-storage">
            <ActivityRow
                icon={Database}
                color="#2563EB"
                title="Storage Shards · RS 16/10"
                meta="reed-solomon · 26 shards distributed"
                status="OK"
                statusColor="#059669"
            />
            <KV k="Encryption"       v="AES-256-GCM"                accent="var(--brand-cyan)" />
            <KV k="Key Management"   v="Client-Side · Zero-Knowledge" />
            <KV k="Replication"      v="3× Geo-Redundant" />
            <KV k="Erasure Coding"   v="Reed-Solomon 16 / 10"       accent="var(--brand-blue)" />
            <KV k="Total Stored"     v="1.42 PB" />
            <KV k="Proof of Storage" v="✓ Verified On-Chain"        accent="var(--success)" />
        </div>
    );
}

// ====== PANEL: AI ===============================================
function PanelAI() {
    return (
        <div data-testid="console-panel-ai">
            <ActivityRow
                icon={Sparkles}
                color="#8B5CF6"
                title="AI Job · Settled in $DCX"
                meta="job · 0x9f2a…c41 · gas: 0.0042 DCX"
                status="Settled"
                statusColor="#059669"
            />
            <KV k="Model"              v="Llama-3.1-70B"     accent="var(--brand-violet)" />
            <KV k="Region"             v="US-East" />
            <KV k="Inference Latency"  v="284 ms" />
            <KV k="GPU Allocation"     v="4 × H100 · 80GB"   accent="var(--brand-blue)" />
            <KV k="Tokens / sec"       v="142 t/s" />
            <KV k="Settled On-Chain"   v="✓ Verified"        accent="var(--success)" />
        </div>
    );
}

// ====== PANEL: LAYER-1 SETTLEMENT ===============================
function PanelLayer1({ block }) {
    return (
        <div data-testid="console-panel-layer1">
            <div className="flex items-center justify-between px-5 py-3 bg-card-muted border-b border-line">
                <div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-faint-ink mb-1">Latest Block</div>
                    <div className="font-heading font-bold text-[18px] text-ink tabular-nums leading-none tracking-tight" data-testid="infra-block-height">
                        #{block.toLocaleString()}
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-faint-ink mb-1">Finalized</div>
                    <div className="inline-flex items-center gap-1.5 text-[11.5px] font-mono font-medium" style={{ color: "var(--success)" }}>
                        <CheckCircle2 size={11} strokeWidth={2} />
                        1.8s avg
                    </div>
                </div>
            </div>

            <KV k="TPS Target"      v="5,000+" />
            <KV k="Validators"      v="24 / 24 Active" />
            <KV k="EVM Compatible"  v="Solidity · Hardhat · Foundry" accent="var(--brand-blue)" />
            <KV k="Gas Token"       v="$DCX"                          accent="var(--brand-violet)" />
            <KV k="Dev Rev Share"   v="5% Protocol-Native"            accent="var(--brand-cyan)" />
        </div>
    );
}

// ====== PANEL: DNS ==============================================
function PanelDNS() {
    return (
        <div data-testid="console-panel-dns">
            <ActivityRow
                icon={Globe2}
                color="#0891B2"
                title="DNS Route · Resolved"
                meta="on-chain · 7 hops · 42 ms"
                status="Resolved"
                statusColor="#059669"
            />
            <KV k="Domain"    v="app.decloudx.eth"     accent="var(--brand-violet)" />
            <KV k="Resolver"  v="DCX Name Service" />
            <KV k="TTL"       v="60s" />
            <KV k="Records"   v="A · AAAA · TXT · CID" accent="var(--brand-blue)" />
            <KV k="Anchored"  v="On-Chain · Layer-1" />
            <KV k="Status"    v="✓ Active"              accent="var(--success)" />
        </div>
    );
}

// ====== Subtitle per tab ========================================
const TAB_SUBTITLES = {
    orchestration: "Hybrid Compute · Autoscaling · Failover · Settlement",
    compute:       "Containerized Workloads · DePIN + Fallback Routing",
    storage:       "Encrypted · Erasure-Coded · Geo-Redundant",
    ai:            "Distributed Inference · Settled in $DCX",
    layer1:        "EVM Chain · On-Chain Finality · $DCX Gas",
    dns:           "DCX Name Service · On-Chain Routing",
};

// ====== Main component ==========================================
export default function InfrastructureConsole() {
    const [active, setActive] = useState("orchestration");
    const [block, setBlock] = useState(2847591);

    // Subtle live block-height tick — increments every ~2s
    useEffect(() => {
        const id = setInterval(() => setBlock((b) => b + Math.floor(1 + Math.random() * 2)), 2000);
        return () => clearInterval(id);
    }, []);

    const activeTab = useMemo(() => TABS.find((t) => t.id === active) || TABS[0], [active]);
    const ActiveIcon = activeTab.icon;

    return (
        <div className="relative w-full max-w-full" data-testid="infra-console">
            {/* subtle background texture */}
            <div className="absolute -inset-3 sm:-inset-6 dot-grid opacity-30 pointer-events-none rounded-3xl" />
            <div className="absolute -inset-4 sm:-inset-10 pointer-events-none">
                <div className="absolute top-[8%] right-[-6%] w-44 h-44 sm:w-72 sm:h-72 rounded-full bg-brand-cyan/8 blur-3xl" />
                <div className="absolute bottom-[2%] left-[-4%] w-52 h-52 sm:w-80 sm:h-80 rounded-full bg-brand-violet/8 blur-3xl" />
            </div>

            {/* ====== SINGLE PRODUCT CONSOLE CARD ====== */}
            <div
                className="surface-strong rounded-2xl overflow-hidden relative"
                style={{ boxShadow: "var(--shadow-lg)" }}
                data-testid="infra-card-console"
            >
                {/* corner gradient accent */}
                <div
                    className="absolute top-0 right-0 w-40 h-40 rounded-full blur-2xl pointer-events-none transition-colors duration-500"
                    style={{ background: `${activeTab.accent}1a` }}
                />

                {/* Window chrome / title bar — now also shows active tab title */}
                <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-line bg-card-muted/60">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex items-center gap-1.5 shrink-0">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                            <ActiveIcon size={12} strokeWidth={1.8} style={{ color: activeTab.accent }} className="shrink-0" />
                            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink-2 truncate">
                                DeCloudX · {activeTab.label}
                            </span>
                        </div>
                    </div>
                    <StatusPill color={activeTab.accent} label="LIVE" />
                </div>

                {/* Tab bar */}
                <div className="border-b border-line bg-card/60 backdrop-blur-sm overflow-x-auto no-scrollbar">
                    <div className="flex items-stretch px-2 min-w-max" role="tablist" data-testid="infra-tabbar">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const isActive = t.id === active;
                            return (
                                <button
                                    key={t.id}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActive(t.id)}
                                    data-testid={`infra-tab-${t.id}`}
                                    className={`relative inline-flex items-center gap-1.5 px-3 py-2.5 text-[11.5px] font-medium whitespace-nowrap transition-colors ${
                                        isActive ? "text-ink" : "text-muted-ink hover:text-ink"
                                    }`}
                                >
                                    <Icon size={12} strokeWidth={1.8} style={{ color: isActive ? t.accent : "currentColor" }} />
                                    {t.label}
                                    {isActive && (
                                        <span
                                            className="absolute left-2 right-2 -bottom-px h-[2px] rounded-full"
                                            style={{ background: t.accent }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Tab content */}
                <div key={active} className="animate-fade-in">
                    {active === "orchestration" && <PanelOrchestration />}
                    {active === "compute"       && <PanelCompute />}
                    {active === "storage"       && <PanelStorage />}
                    {active === "ai"            && <PanelAI />}
                    {active === "layer1"        && <PanelLayer1 block={block} />}
                    {active === "dns"           && <PanelDNS />}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-4 py-2 bg-card-muted border-t border-line">
                    <div className="inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.18em] text-faint-ink">
                        <Zap size={10} strokeWidth={2} className="text-brand-cyan" />
                        Updated · 2s ago
                    </div>
                    <span className="inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-[0.16em] text-ink-2">
                        View Explorer <ArrowUpRight size={10} strokeWidth={2} />
                    </span>
                </div>
            </div>
        </div>
    );
}
